import { library } from '@fortawesome/fontawesome-svg-core';
import { faDownload, faList, faSortAmountDown, faTh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TranslationKey, useTranslator } from '@Localization';
import { Button, Dropdown, ListDivider, Menu, MenuButton, MenuItem } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_ListModeButton } from '../../../../design/buttons/S_ListModeButton';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { ListViewMode } from '../../user/user.store';
import { IProjectListItem } from '../projects.store';

library.add(faSortAmountDown, faTh, faList, faDownload);

interface IProjectsListControlsProps {
  viewMode: ListViewMode;
  orderBy: string;
  orderDirection: string;
  onViewModeChange: (mode: ListViewMode) => Promise<void>;
  onOrderByChange: (order: string) => Promise<void>;
  onOrderDirectionChange: (direction: string) => Promise<void>;
}

interface IFilterItemProps {
  fieldName: keyof IProjectListItem;
  label: TranslationKey;
  orderBy: string;
  onOrderByChange(order: string): Promise<void>;
}

const FilterItem = observer((props: IFilterItemProps): ReactElement => {
  const { t } = useTranslator();

  return (
    <MenuItem
      onClick={(): void => {
        void props.onOrderByChange(props.fieldName);
      }}
      selected={props.orderBy === props.fieldName}
    >
      {t(props.label)}
    </MenuItem>
  );
});

export const ProjectsListControls = observer((props: IProjectsListControlsProps): ReactElement => {
  const { t } = useTranslator();

  return (
    <RowLayout size='md'>
      {props.viewMode === ListViewMode.Tiles && (
        <Dropdown>
          <MenuButton
            slots={{ root: Button }}
            slotProps={{ root: { variant: 'outlined', color: 'primary', size: 'md' } }}
          >
            <FontAwesomeIcon icon={faSortAmountDown} />
          </MenuButton>
          <Menu>
            <FilterItem
              fieldName='name'
              label='name'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='modifiedDate'
              label='modified'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='status'
              label='status'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='imagesCount'
              label='images'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='awaitingCount'
              label='awaiting'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='annotatedCount'
              label='annotated'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='reviewedCount'
              label='reviewed'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='progress'
              label='progress'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <FilterItem
              fieldName='usersCount'
              label='workers'
              orderBy={props.orderBy}
              onOrderByChange={props.onOrderByChange}
            />
            <ListDivider />
            <MenuItem
              onClick={() => {
                void props.onOrderDirectionChange('ASC');
              }}
              selected={props.orderDirection.toUpperCase() === 'ASC'}
            >
              {t('ascending')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                void props.onOrderDirectionChange('DESC');
              }}
              selected={props.orderDirection.toUpperCase() === 'DESC'}
            >
              {t('descending')}
            </MenuItem>
          </Menu>
        </Dropdown>
      )}
      <S_ListModeButton
        viewMode={props.viewMode}
        onChange={props.onViewModeChange}
      />
    </RowLayout>
  );
});
